<script lang="ts" setup>
import { ref } from 'vue'

import Button from '@satch-ui/components/button/Button.vue'
import { DrawerContent, DrawerOverlay } from '@satch-ui/primitives/drawer'
import PopoverContent from '@satch-ui/primitives/popover/PopoverContent.vue'
import { t } from '@satch-ui/translations/translations'
import { useLocationStore } from '@shared/global-store/stores/location/location'
import type { Filter } from '@storefront-satch/components/molecules/filter/types'
import { createReusableTemplate } from '@vueuse/core'

import {
  Drawer,
  DrawerTrigger,
  Popover,
  PopoverTrigger,
} from '@fond-of/fondof-ui'
import {
  ArrowUpDownIcon,
  ChevronIcon,
  CloseThinIcon,
} from '@fond-of/satch-icons'

const props = defineProps<{ filter: Omit<Filter, 'type'> }>()

const isFilterOpen = ref(false)

const locationStore = useLocationStore()

const handleOpenChange = (openState: boolean) => {
  isFilterOpen.value = openState
}

const amountOfSelectedValues = locationStore.location?.searchParams.getAll(
  props.filter.id
)?.length

const [DefineTriggerTemplate, TriggerTemplate] = createReusableTemplate()
</script>

<template>
  <DefineTriggerTemplate>
    <span class="font-sans-semi-bold text-primary text-sm">
      {{ props.filter.label }}
    </span>
    <span
      v-if="amountOfSelectedValues > 0 && props.filter.name !== 'sort'"
      class="bg-primary font-sans-semi-bold text-2xs inline-flex size-[18px] items-center justify-center rounded-full text-center text-white md:text-xs"
    >
      <span>{{ amountOfSelectedValues }}</span>
    </span>
  </DefineTriggerTemplate>

  <div
    :id="`popover-content-${props.filter.name}`"
    :class="[
      'relative hidden md:block',
      'after:border-x-primary after:absolute after:z-50 after:-mt-1 after:hidden after:h-[26px] after:w-full after:border-x-2 after:bg-white after:content-[\'\'] has-[div]:after:block',
    ]"
  >
    <Popover>
      <PopoverTrigger
        :class="[
          'border-primary group border-2 px-4 py-1.5 ring-offset-2 focus:ring-2',
          'flex w-full items-center justify-center gap-1',
        ]"
      >
        <TriggerTemplate />
        <ChevronIcon
          v-if="props.filter.name !== 'sort'"
          class="text-primary size-4 transition-all group-data-[state='open']:rotate-180"
        />
        <ArrowUpDownIcon
          v-else
          class="text-primary size-4"
        />
      </PopoverTrigger>

      <PopoverContent
        :class="[
          'mt-5 flex size-full flex-col bg-white p-5',
          'max-h-[500px] min-w-[360px] max-w-[360px]',
          'border-primary border-2',
        ]"
        :to="`#popover-content-${props.filter?.name}`"
        align="start"
        sticky="partial"
      >
        <div class="scrollbar-primary box-content w-full overflow-y-auto">
          <slot name="form-input" />
        </div>

        <div class="mt-5 w-full bg-white">
          <Button
            class="w-full text-white"
            type="submit"
          >
            {{ t('apply') }}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  </div>

  <div
    :id="`drawer-content-${props.filter.name}`"
    class="block md:hidden"
  >
    <Drawer
      :open="isFilterOpen"
      @update:open="handleOpenChange"
    >
      <DrawerTrigger
        class="border-primary inline-flex w-full items-center justify-center gap-1 border-2 px-4 py-1.5"
      >
        <TriggerTemplate />
        <ChevronIcon
          v-if="props.filter.name !== 'sort'"
          class="text-primary size-4"
        />
        <ArrowUpDownIcon
          v-else
          class="text-primary size-4"
        />
      </DrawerTrigger>

      <DrawerContent
        :class="[
          'isolate flex w-full flex-col rounded-md bg-white px-3.5 pt-3.5',
          'max-h-[480px] pb-[3.7rem]',
        ]"
        :to="`#drawer-content-${props.filter?.name}`"
      >
        <template #overlay>
          <DrawerOverlay />
        </template>

        <CloseThinIcon
          class="absolute right-4 size-8"
          @click="() => handleOpenChange(false)"
        />

        <span class="font-sans-semi-bold mb-1.5 mt-5 text-xs">
          {{ filter.label }}
        </span>

        <div class="max-h-[400px] w-full overflow-y-scroll">
          <slot name="form-input" />
        </div>

        <div
          class="absolute bottom-0 left-0 isolate w-full bg-white px-4 pb-2 pt-2"
        >
          <Button
            class="w-full text-white"
            type="submit"
          >
            {{ t('apply') }}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  </div>
</template>
